/** Assignment-state and driver-copy judgements for the monitoring list. */
import type { DrbOption } from "@/hooks/useCollections"
import type { Tone } from "@/lib/tone"

export interface PlannedDriver {
  name: string
  personId: string | null
}

export interface AssignmentState {
  tone: Tone
  label: string
}

/** Driver and vehicle make a route runnable; trailers never count; a second driver counts only when crewSize > 1. */
export function assignmentState(
  driverName: string | null,
  vehicleName: string | null,
  crewSize: number = 1,
  secondDriverName: string | null = null
): AssignmentState {
  const wantsSecond = crewSize > 1
  const driversComplete = !!driverName && (!wantsSecond || !!secondDriverName)
  const anyDriver = !!driverName || (wantsSecond && !!secondDriverName)

  if (driversComplete && vehicleName) return { tone: "success", label: "Assigned" }
  if (anyDriver || vehicleName) return { tone: "warning", label: "Part-assigned" }
  return { tone: "neutral", label: "Unassigned" }
}

/**
 * Matches on person id only, never name: resourcing and the CRM spell names differently.
 * Ids are compared as trimmed strings (text in the shift, number in the CRM).
 */
export function matchPlannedDriverOption(
  planned: PlannedDriver | undefined,
  driverByPersonId: Map<string, DrbOption>
): DrbOption | undefined {
  const personId = planned?.personId?.toString().trim()
  if (!personId) return undefined
  return driverByPersonId.get(personId)
}

export function indexDriversByPersonId(drivers: DrbOption[]): Map<string, DrbOption> {
  const map = new Map<string, DrbOption>()
  for (const option of drivers) {
    const personId = option.personId?.toString().trim()
    if (personId) map.set(personId, option)
  }
  return map
}

/** Copies the DRB option label (what the Driver picker filters on), else the shift's name. */
export function plannedDriverClipboardValue(
  planned: PlannedDriver,
  option: DrbOption | undefined
): string {
  return option?.name ?? planned.name
}

/** Minutes before planned start that an unassigned row turns "soon". */
export const ASSIGNMENT_URGENCY_MINUTES = 60

export type AssignmentUrgency = "none" | "soon" | "overdue"

/** Never urgent once fully assigned or once the route has actually started, even with an empty driver cell. */
export function assignmentUrgency(
  state: AssignmentState,
  plannedStart: Date,
  actualStart: Date | undefined,
  now: Date
): AssignmentUrgency {
  if (state.label === "Assigned") return "none"
  if (actualStart) return "none"
  const minutesToStart = (plannedStart.getTime() - now.getTime()) / 60_000
  if (minutesToStart < 0) return "overdue"
  if (minutesToStart <= ASSIGNMENT_URGENCY_MINUTES) return "soon"
  return "none"
}

export interface CoverNotChecked {
  label: string
  why: string
}

/**
 * Why the cover check was out of scope for a route, or null if it really found no shift.
 * Mirrors the planning list's notCheckedReason; no rota-horizon case since this list is today only.
 */
export function coverNotCheckedReason(route: {
  status: string
  isAdhoc?: boolean
  actualStartTime?: Date
}): CoverNotChecked | null {
  if (route.status === "cancelled") {
    return { label: "Cancelled", why: "A cancelled route has nothing to staff" }
  }
  if (route.isAdhoc) {
    return {
      label: "Ad-hoc",
      why: "Created on the day, so no shift was booked against it and cover is not checked",
    }
  }
  if (route.actualStartTime) {
    return { label: "Started", why: "The route has gone out; cover is no longer reported" }
  }
  return null
}

export type DriverSeat = "driver" | "second_driver"

/**
 * Null when unmatched or already on the route; else first empty seat (primary before mate);
 * when all seats are taken, replaces the primary.
 */
export function plannedDriverSeat(
  option: DrbOption | undefined,
  driverOptionId: string | null,
  secondDriverOptionId: string | null,
  crewSize: number = 1
): DriverSeat | null {
  if (!option) return null
  if (option.id === driverOptionId || option.id === secondDriverOptionId) return null
  if (!driverOptionId) return "driver"
  if (crewSize > 1 && !secondDriverOptionId) return "second_driver"
  return "driver"
}
